import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
    OnChanges,
} from '@angular/core';
import moment from 'moment';
import { FormControl } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { shareReplay } from 'rxjs';
import { SHARE_REPLAY_SETTINGS } from '@app/configuration.service';
import { Chart } from 'angular-highcharts';
import 'moment/locale/ru';
import { BazisAnalyticService } from '../../service/analytic.service';
import { BazisMediaQueryService } from '@bazis/shared/services/media-query.service';

@Component({
    selector: 'bazis-analytics-date-diagram',
    templateUrl: './date-diagram.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisDateDiagramComponent implements OnInit, OnChanges {
    @Input()
    groupData: any;

    @Input()
    selectedValues: string[];

    @Input()
    settings: { mode: 'dates' | 'months' | 'years' } | null = null;

    @Output()
    select = new EventEmitter();

    readonly currentDay = moment().date();

    readonly currentMonth = moment().month();

    readonly currentYear = moment().year();

    readonly days = [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
        26, 27, 28, 29, 30, 31,
    ];

    readonly daysCategories = this.days.map((day) => day.toString());

    readonly months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    readonly monthsCategories = this.months.map((month) => moment(month, 'M').format('MMMM'));

    readonly monthsList = this.months.map((month) => {
        return { id: month, name: moment(month, 'M').format('MMMM') };
    });

    readonly monthsTabs = this.months.map((month) => {
        return {
            id: month,
            titleKey: `months.long.${month}`,
        };
    });

    get years() {
        return Object.keys(this.groupData);
    }

    get yearsList() {
        return this.years.map((year) => {
            return { id: year.toString(), name: year };
        });
    }

    get yearsTabs() {
        return this.years.map((year) => {
            return { id: year.toString(), title: year };
        });
    }

    readonly selectCtrl = new FormControl(this.currentYear.toString());

    selectedMonthCtrl = new FormControl(this.currentMonth + 1);

    get selectedMonth() {
        return this.selectedMonthCtrl.value;
    }

    set selectedMonth(value: number) {
        this.selectedMonthCtrl.setValue(value);
    }

    selectedMonthChanges$ = this.selectedMonthCtrl.valueChanges.pipe(
        tap((value) => {
            const year = this.selectCtrl.value;
            const groupData = this.groupData[year][value];

            this.drawChart(groupData, this.daysCategories);
            this.onChartStateChanged('date', this.selectCtrl.value, value);
        }),
    );

    changes$ = this.selectCtrl.valueChanges.pipe(
        tap((year) => {
            this.selectedMonth = Number(year) === this.currentYear ? this.currentMonth + 1 : 1;
            const groupData = this.groupData[year][this.selectedMonth];

            this.drawChart(groupData, this.daysCategories);
            this.onChartStateChanged('date', this.selectCtrl.value, this.selectedMonth);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    selectedYearCtrl = new FormControl(this.currentYear.toString());

    get selectedYear() {
        return this.selectedYearCtrl.value;
    }

    set selectedYear(value: string) {
        this.selectedYearCtrl.setValue(value);
    }

    selectedYearChanges$ = this.selectedYearCtrl.valueChanges.pipe(
        tap((value) => {
            const groupData = this.groupData[value];

            this.drawChart(groupData, this.monthsCategories, 65);
            this.onChartStateChanged('month', value);
        }),
    );

    chart: Chart | null = null;

    constructor(
        private readonly analyticService: BazisAnalyticService,
        private readonly mqService: BazisMediaQueryService,
    ) {
        moment.locale('ru');
    }

    ngOnChanges(): void {
        if (this.groupData && this.settings) {
            if (this.settings.mode === 'dates') {
                this.drawChart(
                    this.groupData?.[this.selectCtrl.value]?.[this.selectedMonth],
                    this.daysCategories,
                );
            } else if (this.settings.mode === 'months') {
                let pointWidth = 65;

                if (this.mqService.inIntervalBreakpoint('xs', 'md')) {
                    pointWidth = 20;
                }
                if (this.mqService.inIntervalBreakpoint('md', 'lg')) {
                    pointWidth = 40;
                }

                this.drawChart(
                    this.groupData?.[this.selectedYear],
                    this.monthsCategories,
                    pointWidth,
                );
            } else {
                this.drawChart(this.groupData, this.years, 175);
            }
        }
    }

    ngOnInit(): void {
        const chartState = this.analyticService.chartState$.value;

        if (chartState) {
            if (chartState?.group === 'date') {
                this.selectedMonth = chartState?.month ?? this.currentMonth + 1;
                this.selectCtrl.setValue(
                    chartState?.year?.toString() ?? this.currentYear.toString(),
                );
            }
            if (chartState?.group === 'month') {
                this.selectedYear = chartState?.year ?? this.currentYear;
            }
        }
    }

    onSelectedMonthChanged(event: number) {
        this.selectedMonth = event;
        const year = this.selectCtrl.value;
        const groupData = this.groupData[year][event];

        this.drawChart(groupData, this.daysCategories);
        this.onChartStateChanged('date', this.selectCtrl.value, this.selectedMonth);
    }

    onSelectedYearChanged(event: string) {
        this.selectedYear = event;
        const groupData = this.groupData[event];

        this.drawChart(groupData, this.monthsCategories, 65);
        this.onChartStateChanged('month', this.selectedYear);
    }

    private drawChart(
        groupData: { [item: string]: number },
        categories: string[],
        pointWidth = 20,
    ) {
        const { max, ...data } = groupData;

        this.chart = new Chart({
            chart: {
                type: 'column',
            },
            title: {
                text: null,
            },
            xAxis: {
                categories,
                crosshair: false,
                accessibility: {
                    description: null,
                },
            },
            yAxis: {
                min: 0,
                max,
                title: {
                    text: null,
                },
                tickAmount: 5,
            },
            legend: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            tooltip: {
                outside: true,
                backgroundColor: 'transparent',
                headerFormat: null,
                pointFormat: '{point.y}',
                shadow: false,
            },
            plotOptions: {
                column: {
                    pointWidth,
                    states: {
                        hover: {
                            enabled: true,
                            brightness: 0,
                            halo: {
                                size: 0,
                            },
                        },
                    },
                    dataLabels: {
                        enabled: false,
                        style: {
                            color: '#00a2ad',
                            fontWeight: 'bold',
                            textOutline: 'none',
                        },
                    },
                },
                series: {
                    events: {
                        click: (event) => {
                            const value = event.point.x + 1;

                            if (!value) return;

                            this.selectDate(value);
                        },
                    },
                },
            },
            series: [
                {
                    data: Object.values(data).map((item, idx) => {
                        let selected;

                        if (this.settings.mode === 'dates') {
                            selected = (this.selectedValues ?? [])
                                .map((date) => Number(date.split('-')[2]) - 1)
                                .includes(idx);
                        } else if (this.settings.mode === 'months') {
                            selected = (this.selectedValues ?? [])
                                .map((date) => Number(date.split('-')[0]) - 1)
                                .includes(idx);
                        } else {
                            const groupDataArr = Object.keys(this.groupData);

                            const selectedIndexes = this.getSelectedIndexes(
                                groupDataArr,
                                this.selectedValues,
                            );

                            selected = selectedIndexes.includes(idx);
                        }

                        return {
                            y: item,
                            selected,
                        };
                    }),
                    type: 'column',
                    color: '#a6dee2',
                    states: {
                        hover: {
                            color: '#00a2ad',
                        },
                        select: {
                            color: '#00a2ad',
                            borderColor: '#00a2ad',
                            borderWidth: 0,
                        },
                    },
                },
            ],
        });
    }

    private selectDate(selected: number) {
        if (this.settings.mode === 'dates') {
            const month = this.selectedMonth > 9 ? this.selectedMonth : `0${this.selectedMonth}`;
            const day = selected > 9 ? selected : `0${selected}`;

            this.select.emit(`${this.selectCtrl.value}-${month}-${day}`);
        } else if (this.settings.mode === 'months') {
            const month = selected > 9 ? selected : `0${selected}`;
            const year = this.selectedYear;

            this.select.emit(`${month}-${year}`);
        } else {
            const selectedValue = Object.keys(this.groupData)[selected - 1];

            this.select.emit(selectedValue);
        }
    }

    private onChartStateChanged(
        group: 'date' | 'month' | 'year',
        year?: number | string,
        month?: number | string,
    ) {
        this.analyticService.chartState$.next({ group, year, month });
    }

    private getSelectedIndexes(allKeys = [], selectedKeys = []): number[] {
        return selectedKeys.map((key) => allKeys.indexOf(key)).filter((index) => index !== -1);
    }
}
